import { Injectable } from "@angular/core";

import * as CubiqTS from "shared/cubiq-model-multi-asset";

import { LoggerService } from "@bfx/logger";
import {
  BfxMultiBlotterService,
  SKIP_INIT_DATA_SERVICE,
} from "../../bfx-base-blotter/bfx-base-blotter.service";
const logger = LoggerService.for(__dirname);

import {
  AlgoRiskTradePrimaryLookupService,
  BMAlgoTradePrimaryLookupService,
} from "shared/cubiq-services/lookup/model/impl/RiskTradePrimaryLookupService";
import { RiskTradeTableDefinitionFactory } from "shared/cubiq-services/model/RiskTradeTableDefinitionFactory";
import {
  DetailedOrderStatusRequestPromiseService,
  OrderDataLookupService,
  OrderDataPrimaryLookupService,
} from "shared/cubiq-services";

export interface LoadAlgoTradesResult {
  hasMissingTrades: boolean;
  loadTradesPromise: Promise<boolean>;
}

/**
 * Service for Risk Trades in Algo Monitor that provides datasets and will load in missing deals.
 *
 * Provides the blotter dataset for the all Algo Monitor Risk Trade blotters. The views themselves
 * will apply external filters based on selected Order Ids.
 */
@Injectable()
export class BfxAlgoRiskTradeBlotterService extends BfxMultiBlotterService {
  /** Set of all order Ids that we have already retrieved from the server */
  private loadedOrderIds: Set<string> = new Set();

  /** Current order Id requests that are in progress */
  private missingPromises: Map<string, Promise<boolean>> = new Map();

  constructor(
    private algoTradeDataService: AlgoRiskTradePrimaryLookupService,
    private bmAlgoTradeDataService: BMAlgoTradePrimaryLookupService,
    private orderLookupService: OrderDataLookupService,
    private orderDataService: OrderDataPrimaryLookupService,
    tableDefinitionService: RiskTradeTableDefinitionFactory,
    private detailedOrderService: DetailedOrderStatusRequestPromiseService,
  ) {
    super(SKIP_INIT_DATA_SERVICE, tableDefinitionService);

    this.createTableDefinition();
    this.primeDataService();
  }

  /**
   * Track all changes from both data providers
   */
  protected override subscribeToSubject(): void {
    this.algoTradeDataService.getModelUpdateSubject().subscribe((data: CubiqTS.DealImpl) => {
      this.onModelUpdate(data);
    });
    this.algoTradeDataService.getModelDeleteSubject().subscribe((data) => {
      this.onModelDelete(data);
    });
    this.orderDataService.getModelUpdateSubject().subscribe((data: CubiqTS.OrderDataImpl) => {
      if (data["evtTypeId"] === CubiqTS.FxLifeCycleEventType.ACTIVE) {
        this.onNewOrder(data);
      }
    });
    this.bmAlgoTradeDataService.getModelUpdateSubject().subscribe((data: CubiqTS.DealImpl) => {
      if (data.orderType === CubiqTS.OrderType.BMALGO) {
        this.onModelUpdate(data);
      }
    });
    this.bmAlgoTradeDataService.getModelDeleteSubject().subscribe((data) => {
      this.onModelDelete(data);
    });
  }

  /**
   *  Primarily used by parent class when checking priming status of all providers
   * @param methodName
   * @param args
   * @returns
   */
  protected override runOnActiveDataService(
    methodName: string,
    ...args
  ): Promise<PromiseSettledResult<any>[]> {
    return Promise.allSettled([
      ...[this.algoTradeDataService[methodName](...args)],
      ...[this.bmAlgoTradeDataService[methodName](...args)],
    ]);
  }

  /**
   * Make sure all trades associated with the specified order Ids are stored locally. At the moment,
   * the service assumes that all order trades need to retrieved form the server - once an order's
   * trades have been requested:
   *  * The request is associated with a single promise that resolved when trades are available
   *  * Each requested order is associated with the request promise
   *  * While waiting for trades to download all subsequent requests will return the original request for checking
   *  * Once trades are downloaded order is marked as present and is never downloaded again
   *
   * There are more complex approaches to determine whether we need to request the trades for an order, and
   * what trades we need to donwload - this is something we can optimise when needed at a later time
   * @param orderIds
   * @returns
   */
  public loadTradesForOrders(orderIds: string[]): LoadAlgoTradesResult {
    const missingOrderIds = orderIds.filter((orderId) => !this.loadedOrderIds.has(orderId));

    let retResult: LoadAlgoTradesResult = {
      hasMissingTrades: false,
      loadTradesPromise: null,
    };

    if (missingOrderIds.length > 0) {
      retResult.hasMissingTrades = true;

      // We now have the set of order Ids that we have not yet retrieved from the server
      const orderPromises: Set<Promise<boolean>> = new Set();
      const toRetrieve = [];

      for (const orderId of missingOrderIds) {
        // Let see which of those orders are already in process of being loaded
        const promise = this.missingPromises.get(orderId);
        if (promise) {
          // Push the promise for every distinct fetch request already underway
          orderPromises.add(promise);
        } else {
          // May be the order has not been filled yet
          const order = this.orderLookupService.getItem(orderId);
          if (order && order.fillState === CubiqTS.FillState.NONE) {
            // Order has no fills so no need to prime fills from the server
            this.loadedOrderIds.add(orderId);
          } else {
            toRetrieve.push(orderId);
          }
        }
      }

      if (toRetrieve.length > 0) {
        // We have some orders without any outstanding fetch requests so need to load them
        const fetchPromise = this.fetchTradesFromServer(toRetrieve);

        orderPromises.add(fetchPromise);
        for (const orderId of toRetrieve) {
          this.missingPromises.set(orderId, fetchPromise);
        }
      }
      retResult.loadTradesPromise = Promise.all(orderPromises).then(
        () => true,
        () => false,
      );
    } else {
      retResult.loadTradesPromise = Promise.resolve(true);
    }
    return retResult;
  }

  /**
   * Send request to the server for all trades for the specified orders, and process
   * their results
   * @param orderIds
   * @returns
   */
  private fetchTradesFromServer(orderIds: string[]): Promise<boolean> {
    logger.out.info(`algo-rtb-s - fetching Trades from server for ${orderIds.length} orders`);
    return this.detailedOrderService.requestAdaptedTradeDeals(orderIds).then(
      (deals: CubiqTS.DealImpl[]) => {
        orderIds.forEach((orderId) => {
          this.loadedOrderIds.add(orderId);
          this.missingPromises.delete(orderId);
        });
        deals.forEach((deal) => {
          if (this.isRiskTrade(deal)) {
            this.onModelUpdate(deal);
          }
        });
        return true;
      },
      (_errors) => {
        orderIds.forEach((orderId) => this.missingPromises.delete(orderId));
        return false;
      },
    );
  }

  /**
   * Is the deal a Risk trade?
   *
   * @param deal
   * @returns
   */
  private isRiskTrade(deal: CubiqTS.DealImpl): boolean {
    return deal.makerDealRef != null || deal.spotReservationFg === true;
  }

  /**
   * When we get a new algo notification, we know that we do not need to go to the server to get any
   * trades for the order - the current trade cahce cache will always have them
   * @param orderData
   */
  private onNewOrder(orderData: CubiqTS.OrderDataImpl) {
    if (
      orderData.orderType === CubiqTS.OrderType.ALGO ||
      orderData.orderType === CubiqTS.OrderType.BMALGO
    ) {
      const orderId = orderData.orderId.toString();
      logger.out.info(`algo-rtb-s - Adding new order to available algo trades: ${orderId} `);
      this.loadedOrderIds.add(orderId);
    }
  }
}
